import React, { useState } from 'react';
import PartnerApplicationDialog from '@/components/landing/PartnerApplicationDialog';
import MediaPartnershipDialog from '@/components/landing/MediaPartnershipDialog';
import { usePageMeta } from '@/hooks/usePageMeta';
import LandingHeader from '@/components/landing/LandingHeader';
import LandingFooter from '@/components/landing/LandingFooter';
import {
  ArrowRight, Download, CheckCircle2, Building2, Truck, Users, Handshake,
  TrendingUp, MousePointerClick, Megaphone, XCircle, RefreshCw, Info,
  DollarSign, BarChart3, FileText, Share2, UserCheck, Award, Gem,
} from 'lucide-react';

const TEAL = '#02857E';
const TEAL_LIGHT = '#0FB5A6';
const INK = '#04211F';

const Shell: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`mx-auto max-w-[1540px] px-5 sm:px-8 ${className}`}>{children}</div>
);

const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.18em]" style={{ color: TEAL }}>
    {children}
  </div>
);

/* ---------------------------------- HERO ---------------------------------- */

const HeroDiagram: React.FC = () => (
  <div className="relative w-full max-w-[640px] mx-auto aspect-[4/3]">
    <svg viewBox="0 0 640 480" className="w-full h-full" role="img" aria-label="Partner network diagram">
      <defs>
        <radialGradient id="pp-glow" cx="50%" cy="45%" r="55%">
          <stop offset="0%" stopColor={TEAL_LIGHT} stopOpacity="0.28" />
          <stop offset="100%" stopColor={TEAL_LIGHT} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="640" height="480" fill="url(#pp-glow)" />
      {[...Array(70)].map((_, i) => {
        const x = (i * 97) % 620 + 10;
        const y = (i * 53) % 460 + 10;
        return <circle key={i} cx={x} cy={y} r={i % 7 === 0 ? 1.8 : 1} fill={TEAL_LIGHT} opacity={i % 3 === 0 ? 0.5 : 0.22} />;
      })}
      <path d="M60 250 Q320 60 580 250" fill="none" stroke={TEAL_LIGHT} strokeOpacity="0.28" strokeDasharray="4 6" />
      <path d="M60 300 Q320 130 580 300" fill="none" stroke={TEAL_LIGHT} strokeOpacity="0.18" strokeDasharray="4 6" />
      {/* connectors */}
      <path d="M320 150 L320 205" stroke="#fff" strokeOpacity="0.5" strokeWidth="1.5" markerEnd="url(#pp-arrow)" />
      <path d="M285 275 Q200 330 160 355" fill="none" stroke="#fff" strokeOpacity="0.45" strokeWidth="1.5" markerEnd="url(#pp-arrow)" />
      <path d="M320 285 L320 350" stroke="#fff" strokeOpacity="0.45" strokeWidth="1.5" markerEnd="url(#pp-arrow)" />
      <path d="M355 275 Q440 330 480 355" fill="none" stroke="#fff" strokeOpacity="0.45" strokeWidth="1.5" markerEnd="url(#pp-arrow)" />
      <defs>
        <marker id="pp-arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 z" fill="#fff" fillOpacity="0.6" />
        </marker>
      </defs>
      <circle cx="320" cy="110" r="38" fill="#04211F" stroke={TEAL_LIGHT} strokeOpacity="0.55" />
      <circle cx="320" cy="245" r="40" fill="#04211F" stroke={TEAL_LIGHT} strokeOpacity="0.7" />
      <circle cx="160" cy="392" r="34" fill="#04211F" stroke={TEAL_LIGHT} strokeOpacity="0.5" />
      <circle cx="320" cy="392" r="34" fill="#04211F" stroke={TEAL_LIGHT} strokeOpacity="0.5" />
      <circle cx="480" cy="392" r="34" fill="#04211F" stroke={TEAL_LIGHT} strokeOpacity="0.5" />
    </svg>

    {/* Node labels + icons layered over the SVG */}
    <NodeLabel x="50%" y="23%" icon={<Building2 className="h-6 w-6" style={{ color: TEAL_LIGHT }} />} title="SHIPPER" sub="Your referral" titleClassName="mt-[2px]" />
    <NodeLabel x="50%" y="51%" icon={<span className="text-white font-black tracking-tight">LR</span>} title="LANERUNNER" sub="The network" titleClassName="mt-[22px]" />
    <NodeLabel x="25%" y="82%" icon={<Truck className="h-5 w-5" style={{ color: TEAL_LIGHT }} />} title="BROKER / CARRIER" sub="Active member" small />
    <NodeLabel x="50%" y="82%" icon={<Truck className="h-5 w-5" style={{ color: TEAL_LIGHT }} />} title="BROKER / CARRIER" sub="Active member" small />
    <NodeLabel x="75%" y="82%" icon={<Truck className="h-5 w-5" style={{ color: TEAL_LIGHT }} />} title="BROKER / CARRIER" sub="Active member" small />

    <div className="absolute left-[63%] top-[41%] text-[10px] font-semibold" style={{ color: TEAL_LIGHT }}>One-time<br />bonus</div>
    <div className="absolute left-[57%] top-[64%] text-[10px] font-semibold" style={{ color: TEAL_LIGHT }}>Recurring revenue<br />share</div>
  </div>
);

const NodeLabel: React.FC<{
  x: string; y: string; icon: React.ReactNode; title: string; sub: string; small?: boolean; className?: string; titleClassName?: string;
}> = ({ x, y, icon, title, sub, small, className = '', titleClassName = '' }) => (
  <div className={`absolute -translate-x-1/2 flex flex-col items-center ${className}`} style={{ left: x, top: y }}>
    <div className="flex items-center justify-center -translate-y-1/2" style={{ height: small ? 30 : 36 }}>{icon}</div>
    <div className={`mt-8 ${small ? 'text-[9px]' : 'text-[10px]'} font-bold tracking-[0.1em] text-white whitespace-nowrap ${titleClassName}`}>{title}</div>
    <div className="text-[9px] text-white/55 whitespace-nowrap">{sub}</div>
  </div>
);

/* --------------------------------- PAGE ---------------------------------- */

const PartnerProgramPage: React.FC = () => {
  usePageMeta({
    fullTitle: true,
    title: 'Partner Program | LaneRunner',
    description:
      'LaneRunner rewards the people who help us grow. Earn recurring revenue share on the network you build, plus one-time bonuses for shipper referrals.',
  });

  const tiers = [
    { name: 'Bronze', rate: '20%', rateLabel: 'Revenue Share', members: '10', color: '#B87333', icon: Award, est: 'Approx. $200/mo' },
    { name: 'Silver', rate: '25%', rateLabel: 'Revenue Share', members: '25', color: '#9AA5AD', icon: Award, est: 'Approx. $625/mo' },
    { name: 'Gold', rate: '35%', rateLabel: 'Revenue Share', members: '50', color: '#E0A800', icon: Award, est: 'Approx. $1,750/mo' },
    { name: 'Platinum', rate: '50%', rateLabel: 'Founding Partner Rate', members: '100+', color: TEAL, icon: Gem, est: 'Approx. $5,000+/mo' },
  ];

  const [applyOpen, setApplyOpen] = useState(false);
  const [applySource, setApplySource] = useState('partner_program_hero');
  const [mediaOpen, setMediaOpen] = useState(false);

  const CtaPair: React.FC<{ dark?: boolean; stacked?: boolean }> = ({ dark, stacked }) => (
    <div className={`flex ${stacked ? 'flex-col' : 'flex-col sm:flex-row'} gap-3`}>
      <button
        type="button"
        onClick={() => {
          setApplySource(stacked ? 'partner_program_final_cta' : 'partner_program_hero');
          setApplyOpen(true);
        }}
        className="inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3.5 text-[15px] font-semibold text-white shadow-sm transition-colors"
        style={{ backgroundColor: TEAL }}
      >
        Apply to Become a Partner
        {stacked && <ArrowRight className="h-4 w-4" />}
      </button>
      <button
        type="button"
        className={`inline-flex items-center justify-center gap-2 rounded-lg border px-6 py-3.5 text-[15px] font-semibold transition-colors ${
          dark ? 'border-white/25 text-white hover:bg-white/5' : 'border-[#DDE3E7] text-[#04211F] hover:bg-[#F5F7F8]'
        }`}
      >
        <Download className="h-4 w-4" />
        Download the Partner Guide (PDF)
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-white" style={{ color: INK }}>
      <LandingHeader activePage="partner-program" />

      {/* ================================ HERO ================================ */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(140deg,#031A1E 0%,#04211F 45%,#062B2A 100%)' }}>
        <Shell className="py-14 sm:py-20 lg:py-24">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <div className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.2em]" style={{ color: TEAL_LIGHT }}>
                LaneRunner Partner Program
              </div>
              <h1 className="mt-5 text-[38px] sm:text-[52px] lg:text-[58px] font-extrabold leading-[1.05] tracking-[-0.02em] text-white">
                Grow the network.<br />Share the opportunity.<br />Get rewarded.
              </h1>
              <p className="mt-6 max-w-[560px] text-[15px] sm:text-base leading-relaxed text-white/70">
                LaneRunner rewards the people who help us grow. Shippers, brokers, and carriers — help them find new
                opportunities, and earn as they join the platform.
              </p>
              <div className="mt-8">
                <CtaPair dark />
              </div>
              <ul className="mt-8 flex flex-wrap gap-x-7 gap-y-3">
                {['Community-driven growth', 'Recurring revenue share', 'One-time shipper bonuses'].map((t) => (
                  <li key={t} className="flex items-center gap-2 text-[13px] text-white/75">
                    <CheckCircle2 className="h-4 w-4" style={{ color: TEAL_LIGHT }} />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <HeroDiagram />
          </div>
        </Shell>
      </section>

      {/* ============================ PHILOSOPHY ============================= */}
      <section className="bg-white py-16 sm:py-20">
        <Shell>
          <div className="grid gap-10 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.4fr)] lg:gap-16 lg:items-center">
            <div>
              <h2 className="text-[28px] sm:text-[34px] font-extrabold leading-[1.15] tracking-[-0.01em]">
                We invest in relationships,<br className="hidden sm:block" /> not ad spend.
              </h2>
              <p className="mt-5 text-[14px] sm:text-[15px] leading-relaxed text-[#4A5A5C]">
                Instead of pouring marketing dollars into clicks and impressions, LaneRunner rewards the people who actually
                introduce the platform to the freight community. We'd rather put that money into the hands of the partners
                building this network with us.
              </p>
            </div>

            <div className="rounded-2xl border border-[#DDE3E7] bg-[#FAFBFC] p-6 sm:p-8 shadow-[0_1px_2px_rgba(4,33,31,0.04)]">
              <div className="grid gap-8 md:grid-cols-[1fr_auto_1fr] md:items-center">
                <div className="text-center">
                  <div className="text-[13px] font-bold">Traditional Approach</div>
                  <div className="mt-1 text-[12px] text-[#7A8A8C]">Ad spend buys clicks.</div>
                  <FlowRow
                    items={[
                      { icon: Megaphone, label: 'Ad Spend' },
                      { icon: MousePointerClick, label: 'Clicks' },
                      { icon: XCircle, label: 'Low Conversion' },
                    ]}
                    muted
                  />
                </div>
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[#DDE3E7] bg-white text-[12px] font-bold text-[#7A8A8C]">
                  vs.
                </div>
                <div className="text-center md:border-l md:border-[#E6EBEE] md:pl-8">
                  <div className="text-[13px] font-bold" style={{ color: TEAL }}>LaneRunner Approach</div>
                  <div className="mt-1 text-[12px] text-[#7A8A8C]">Partners build lasting networks.</div>
                  <FlowRow
                    items={[
                      { icon: Users, label: 'Partners' },
                      { icon: Handshake, label: 'Real Relationships' },
                      { icon: TrendingUp, label: 'Community Growth' },
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>
        </Shell>
      </section>

      {/* ========================= PROGRAM 1 — TIERS ========================= */}
      <section className="border-y border-[#E6EBEE] bg-[#F5F7F8] py-16 sm:py-20">
        <Shell>
          <div className="grid gap-10 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.6fr)] lg:gap-14">
            <div>
              <Eyebrow>Program 1 · Network Rewards</Eyebrow>
              <h2 className="mt-4 text-[28px] sm:text-[34px] font-extrabold leading-[1.15] tracking-[-0.01em]">
                Earn every month on<br className="hidden sm:block" /> the network you build.
              </h2>
              <p className="mt-5 text-[14px] sm:text-[15px] leading-relaxed text-[#4A5A5C]">
                You already invite the brokers and carriers you trust into your private network on LaneRunner. Now that network
                can earn. When the brokers and carriers you bring in subscribe, you can receive a share of that subscription
                each month, for as long as they stay active.
              </p>
            </div>

            <div>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {tiers.map((t) => {
                  const Icon = t.icon;
                  const isTop = t.name === 'Platinum';
                  return (
                    <div
                      key={t.name}
                      className={`rounded-xl border bg-white p-5 text-center shadow-[0_1px_3px_rgba(4,33,31,0.06)] ${
                        isTop ? 'border-[#02857E]' : 'border-[#DDE3E7]'
                      }`}
                    >
                      <Icon className="mx-auto h-9 w-9" style={{ color: t.color }} strokeWidth={1.6} />
                      <div className="mt-3 text-[15px] font-bold">{t.name}</div>
                      <div className="mt-3 text-[34px] font-extrabold leading-none tracking-[-0.02em]">{t.rate}</div>
                      <div className="mt-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#7A8A8C]">{t.rateLabel}</div>
                      <div className="my-4 h-px bg-[#E6EBEE]" />
                      <div className="text-[24px] font-extrabold leading-none">{t.members}</div>
                      <div className="mt-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#7A8A8C]">Active Paying Members</div>
                      <div
                        className="mt-4 rounded-md px-3 py-2 text-[12px] font-semibold"
                        style={{ backgroundColor: 'rgba(2,133,126,0.08)', color: TEAL }}
                      >
                        {t.est}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 grid gap-4 rounded-xl border border-[#DDE3E7] bg-white p-5 sm:grid-cols-3">
                <MiniNote icon={Users} title="How it adds up" body="Your tier rate applies to every active paying broker or carrier in your network — not just the next one. Reach a higher tier and your whole network earns at the higher rate." />
                <MiniNote icon={RefreshCw} title="Recurring, not one-time" body="As long as your members stay active on the platform, you can keep earning each month." />
                <MiniNote icon={Info} title="Founding Partner rates" body="Illustrative examples based on current subscription pricing." />
              </div>
              <p className="mt-4 text-[12px] leading-relaxed text-[#7A8A8C]">
                LaneRunner is a single-level referral program. Partners earn only on qualifying customers they directly refer,
                and do not receive commissions on referrals made by those customers.
              </p>
            </div>
          </div>
        </Shell>
      </section>

      {/* ====================== PROGRAM 2 — SHIPPER REFS ===================== */}
      <section className="bg-white py-16 sm:py-20">
        <Shell>
          <div className="grid gap-10 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.6fr)] lg:gap-14">
            <div>
              <Eyebrow>Program 2 · Shipper Referrals</Eyebrow>
              <h2 className="mt-4 text-[28px] sm:text-[34px] font-extrabold leading-[1.15] tracking-[-0.01em]">
                Know another shipper?<br className="hidden sm:block" /> Get paid to introduce them.
              </h2>
              <p className="mt-5 text-[14px] sm:text-[15px] leading-relaxed text-[#4A5A5C]">
                Bring another shipper to LaneRunner and earn a one-time referral bonus once they meet the qualifying milestone.
                They start their own network and their own rewards. You receive your bonus.
              </p>
            </div>

            <div>
              <div className="grid gap-4 sm:grid-cols-2">
                <BonusCard
                  amount="$500"
                  body="Refer a shipper who gets 20+ brokers or carriers active in their network"
                  icon={Users}
                />
                <BonusCard
                  amount="$1,000"
                  body="Refer a shipper who reaches $1,000 in monthly subscriptions"
                  icon={FileText}
                />
              </div>
              <p className="mt-4 text-[12px] leading-relaxed text-[#7A8A8C]">
                One-time referral bonuses, paid when the referred shipper meets the qualifying milestone. Current amounts and
                requirements are governed by the Partner Program Terms.
              </p>
            </div>
          </div>
        </Shell>
      </section>

      {/* ============================ EVERYONE WINS ========================== */}
      <section className="border-y border-[#E6EBEE] bg-[#F5F7F8] py-16 sm:py-20">
        <Shell>
          <div className="grid gap-10 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.6fr)] lg:gap-14 lg:items-center">
            <h2 className="text-[28px] sm:text-[34px] font-extrabold leading-[1.15] tracking-[-0.01em]">
              One program.<br className="hidden sm:block" /> Everyone comes out ahead.
            </h2>
            <div className="grid gap-8 sm:grid-cols-3">
              <WinItem
                icon={DollarSign}
                title="You get paid"
                body="Recurring income on the network you were already building."
              />
              <WinItem
                icon={Users}
                title="Your referrals get a better platform"
                body="Brokers and carriers win direct shipper opportunities; shippers source freight with more competition."
              />
              <WinItem
                icon={BarChart3}
                title="LaneRunner grows through real relationships"
                body="Not ad spend."
              />
            </div>
          </div>

          <div className="mt-14">
            <div className="text-center text-[13px] font-bold">Who you can bring aboard</div>
            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              <CategoryCard icon={Building2} title="Refer a shipper" body="Help them source freight with more competition and better rate visibility." />
              <CategoryCard icon={Users} title="Refer a broker" body="Help them gain direct access to shipper freight and new business." />
              <CategoryCard icon={Truck} title="Refer a carrier" body="Help them find more freight and compete for more opportunities." />
            </div>
          </div>
        </Shell>
      </section>

      {/* ============================= HOW IT WORKS ========================== */}
      <section className="bg-white py-16 sm:py-20">
        <Shell>
          <Eyebrow>How it works</Eyebrow>
          <h2 className="mt-3 text-[28px] sm:text-[34px] font-extrabold tracking-[-0.01em]">Four simple steps.</h2>

          <div className="mt-8 grid gap-4 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] lg:items-stretch">
            {[
              { n: '1', icon: FileText, title: 'Apply and get approved', body: 'Receive your unique referral link and network ID.' },
              { n: '2', icon: Share2, title: 'Share LaneRunner', body: 'Invite the brokers, carriers, and shippers in your network.' },
              { n: '3', icon: UserCheck, title: 'They join and subscribe', body: 'Your network starts moving freight on the platform.' },
              { n: '4', icon: TrendingUp, title: 'You earn', body: 'Recurring share on your network, plus one-time bonuses for shippers you bring aboard.' },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <React.Fragment key={s.n}>
                  <div className="rounded-xl border border-[#DDE3E7] bg-white p-6 shadow-[0_1px_3px_rgba(4,33,31,0.05)]">
                    <div className="flex items-start justify-between">
                      <span className="text-[32px] font-extrabold leading-none" style={{ color: TEAL }}>{s.n}</span>
                      <Icon className="h-9 w-9" style={{ color: TEAL }} strokeWidth={1.5} />
                    </div>
                    <div className="mt-6 text-[15px] font-bold">{s.title}</div>
                    <p className="mt-2 text-[13px] leading-relaxed text-[#4A5A5C]">{s.body}</p>
                  </div>
                  {i < 3 && (
                    <div className="hidden lg:flex items-center justify-center px-1 text-[#B7C3C6]">
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </Shell>
      </section>

      {/* ============================== FINAL CTA ============================ */}
      <section className="bg-white pb-16 sm:pb-20">
        <Shell>
          <div
            className="relative overflow-hidden rounded-2xl px-6 py-12 sm:px-12 sm:py-14"
            style={{ background: 'linear-gradient(120deg,#031A1E 0%,#04211F 50%,#062B2A 100%)' }}
          >
            <div className="grid gap-10 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.9fr)] lg:items-center">
              <div>
                <h2 className="text-[30px] sm:text-[38px] font-extrabold leading-[1.1] tracking-[-0.02em] text-white">
                  Build it once.<br />Get rewarded.
                </h2>
                <p className="mt-5 max-w-[620px] text-[14px] sm:text-[15px] leading-relaxed text-white/70">
                  LaneRunner is free for shippers. Build your network, let brokers and carriers compete on your freight, and turn
                  it into recurring income — the bigger it grows, the more you can earn.
                </p>
              </div>
              <div className="lg:border-l lg:border-white/10 lg:pl-10">
                <CtaPair dark stacked />
              </div>
            </div>
          </div>

          <p className="mx-auto mt-8 max-w-[900px] text-center text-[11.5px] leading-relaxed text-[#7A8A8C]">
            Illustrative earnings examples are based on the current $99.99 monthly subscription and the stated number of
            qualifying active paid members. Actual earnings depend on referral activity, customer retention, eligibility, and
            other factors. Current commission rates, bonus amounts, qualification requirements, and grandfathering provisions
            are governed by the LaneRunner Partner Program Terms.
          </p>
        </Shell>
      </section>

      {/* =================== CREATOR & MEDIA PARTNERSHIPS =================== */}
      <section className="border-t border-[#E3E9EA] bg-[#F7FAFA] py-16 sm:py-20">
        <Shell>
          <div className="mx-auto max-w-[1000px] rounded-2xl border border-[#DDE3E7] bg-white p-7 shadow-sm sm:p-10">
            <Eyebrow>Creator &amp; Media Partnerships</Eyebrow>
            <h2
              className="mt-4 text-[26px] sm:text-[34px] font-extrabold leading-[1.12] tracking-[-0.02em]"
              style={{ color: INK }}
            >
              Have an industry audience?
            </h2>
            <p className="mt-5 max-w-[760px] text-[14px] sm:text-[15px] leading-relaxed text-[#4A5A5C]">
              If you're building a following in freight, logistics, trucking, or supply chain — or already have an
              established audience — LaneRunner would like to talk. We work with creators, industry pages, newsletters,
              podcasts, communities, and other freight-focused media partners on paid advertising and promotional
              opportunities. Tell us about your audience and how you'd like to work together.
            </p>
            <button
              type="button"
              onClick={() => setMediaOpen(true)}
              className="mt-7 inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3.5 text-[15px] font-semibold text-white shadow-sm transition-colors"
              style={{ backgroundColor: TEAL }}
            >
              Talk to LaneRunner →
            </button>
          </div>
        </Shell>
      </section>

      <LandingFooter />

      <PartnerApplicationDialog open={applyOpen} onOpenChange={setApplyOpen} source={applySource} />
      <MediaPartnershipDialog open={mediaOpen} onOpenChange={setMediaOpen} source="partner_program_media" />

    </div>
  );
};

/* ------------------------------ Sub-components ---------------------------- */

const FlowRow: React.FC<{ items: { icon: React.ElementType; label: string }[]; muted?: boolean }> = ({ items, muted }) => (
  <div className="mt-5 flex items-start justify-center gap-2">
    {items.map((it, i) => {
      const Icon = it.icon;
      return (
        <React.Fragment key={it.label}>
          <div className="flex w-[72px] flex-col items-center">
            <div
              className="flex h-11 w-11 items-center justify-center rounded-full border bg-white"
              style={{ borderColor: muted ? '#DDE3E7' : 'rgba(2,133,126,0.35)' }}
            >
              <Icon className="h-5 w-5" style={{ color: muted ? '#9AA5AD' : TEAL }} strokeWidth={1.6} />
            </div>
            <div className="mt-2 text-[10.5px] font-semibold leading-tight text-[#4A5A5C]">{it.label}</div>
          </div>
          {i < items.length - 1 && <ArrowRight className="mt-4 h-3.5 w-3.5 shrink-0 text-[#B7C3C6]" />}
        </React.Fragment>
      );
    })}
  </div>
);

const MiniNote: React.FC<{ icon: React.ElementType; title: string; body: string }> = ({ icon: Icon, title, body }) => (
  <div className="flex gap-3">
    <Icon className="mt-0.5 h-5 w-5 shrink-0" style={{ color: TEAL }} strokeWidth={1.6} />
    <div>
      <div className="text-[12.5px] font-bold">{title}</div>
      <p className="mt-1 text-[12px] leading-relaxed text-[#7A8A8C]">{body}</p>
    </div>
  </div>
);

const BonusCard: React.FC<{ amount: string; body: string; icon: React.ElementType }> = ({ amount, body, icon: Icon }) => (
  <div className="relative rounded-xl border border-[#DDE3E7] bg-[#FAFBFC] p-6 shadow-[0_1px_3px_rgba(4,33,31,0.05)]">
    <span
      className="inline-flex rounded-md px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.08em] text-white"
      style={{ backgroundColor: TEAL }}
    >
      One-time bonus
    </span>
    <div className="mt-5 flex items-end justify-between gap-4">
      <div>
        <div className="text-[44px] font-extrabold leading-none tracking-[-0.03em]" style={{ color: TEAL }}>{amount}</div>
        <p className="mt-3 max-w-[240px] text-[12.5px] leading-relaxed text-[#4A5A5C]">{body}</p>
      </div>
      <Icon className="h-12 w-12 shrink-0" style={{ color: 'rgba(2,133,126,0.35)' }} strokeWidth={1.3} />
    </div>
  </div>
);

const WinItem: React.FC<{ icon: React.ElementType; title: string; body: string }> = ({ icon: Icon, title, body }) => (
  <div className="sm:border-l sm:border-[#DDE3E7] sm:pl-5 first:sm:border-l-0 first:sm:pl-0">
    <Icon className="h-8 w-8" style={{ color: TEAL }} strokeWidth={1.5} />
    <div className="mt-3 text-[14px] font-bold leading-snug">{title}</div>
    <p className="mt-2 text-[12.5px] leading-relaxed text-[#4A5A5C]">{body}</p>
  </div>
);

const CategoryCard: React.FC<{ icon: React.ElementType; title: string; body: string }> = ({ icon: Icon, title, body }) => (
  <div className="flex gap-4 rounded-xl border border-[#DDE3E7] bg-white p-5 shadow-[0_1px_3px_rgba(4,33,31,0.05)]">
    <Icon className="h-9 w-9 shrink-0" style={{ color: TEAL }} strokeWidth={1.4} />
    <div>
      <div className="text-[14px] font-bold">{title}</div>
      <p className="mt-1.5 text-[12.5px] leading-relaxed text-[#4A5A5C]">{body}</p>
    </div>
  </div>
);

export default PartnerProgramPage;
